import { useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { useAppDispatch, useAppSelector } from "@/app/store";
import {
  selectSidebarOpen,
  selectSidebarHovered,
  selectDirection,
  toggleSidebar,
  setSidebarHovered,
} from "@/app/store/slices/ui.slice";
import { selectUser, selectUserPermissions } from "@/app/store/slices/auth.slice";
import {
  navigationConfig,
  bottomNavigationConfig,
  filterSectionsByPermissions,
  filterNavByPermissions,
  isBranchAccountUser,
} from "../../config/navigation.config";
import { SidebarProvider } from "./SidebarContext";
import { SidebarSection } from "./SidebarSection";
import { SidebarItem } from "./SidebarItem";
import { SidebarLogo } from "./SidebarLogo";
import { SIDEBAR_CONFIG } from "@/shared/constants";

export function Sidebar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isOpen = useAppSelector(selectSidebarOpen);
  const isHovered = useAppSelector(selectSidebarHovered);
  const direction = useAppSelector(selectDirection);
  const user = useAppSelector(selectUser);
  const userPermissions = useAppSelector(selectUserPermissions);
  const isBranchUser = isBranchAccountUser(user);

  const sidebarRef = useRef<HTMLElement>(null);

  const filteredSections = filterSectionsByPermissions(
    navigationConfig,
    userPermissions,
    isBranchUser,
  );
  const filteredBottomNav = filterNavByPermissions(
    bottomNavigationConfig,
    userPermissions,
    isBranchUser,
  );

  // Effective open state (considering hover)
  const effectiveOpen = isOpen || isHovered;

  // Handle toggle
  const handleToggle = useCallback(() => {
    dispatch(toggleSidebar());
    dispatch(setSidebarHovered(false));
  }, [dispatch]);

  // Arrow icon based on direction and state
  const ArrowIcon =
    direction === "rtl"
      ? effectiveOpen
        ? ArrowRight
        : ArrowLeft
      : effectiveOpen
      ? ArrowLeft
      : ArrowRight;

  return (
    <SidebarProvider>
      <aside
        ref={sidebarRef}
        //onMouseEnter={handleMouseEnter}
        //onMouseLeave={handleMouseLeave}
        className={cn(
          "relative",
          "fixed top-0 bottom-0 z-40 flex flex-col",
          "bg-grey-50",
          "transition-all duration-300 ease-in-out",
          direction === "rtl" ? "right-0 border-l" : "left-0 border-r",
          effectiveOpen
            ? `w-[${SIDEBAR_CONFIG.WIDTH_EXPANDED}px]`
            : `w-[${SIDEBAR_CONFIG.WIDTH_COLLAPSED}px]`
        )}
        style={{
          width: effectiveOpen
            ? SIDEBAR_CONFIG.WIDTH_EXPANDED
            : SIDEBAR_CONFIG.WIDTH_COLLAPSED,
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-3 border-b">
          <SidebarLogo isOpen={effectiveOpen} />
        </div>

        {/* Toggle Button */}
        <button
          onClick={handleToggle}
          className={cn(
            "absolute top-[1rem] -end-[0.8rem] z-50 p-1.5 bg-white rounded-full shadow-md ",

            "transition-colors duration-200",
            !effectiveOpen && "mx-auto"
          )}
          aria-label={isOpen ? t("common.close") : t("common.open")}
        >
          <ArrowIcon className="w-4 h-4" />
        </button>

        {/* Navigation */}
        <nav className="flex-1 gap-2 overflow-y-auto overflow-x-hidden py-4 custom-scrollbar">
          {filteredSections.map((section) => (
            <SidebarSection
              key={section.id}
              section={section}
              isOpen={effectiveOpen}
              currentPath={location.pathname}
            />
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div
          className={cn(
            "border-t border-sidebar-border py-2 px-4",
            isOpen ? "px-4" : "px-2"
          )}
        >
          {filteredBottomNav.map((item) => (
            <SidebarItem
              key={item.id}
              item={item}
              isOpen={effectiveOpen}
              isActive={location.pathname === item.href}
              isBottomItem
            />
          ))}
        </div>
      </aside>
    </SidebarProvider>
  );
}
